import { useEffect, useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Select, Textarea } from '@ds/forms';
import { SegmentedControl } from '@ds/navigation';
import { centavosDoTexto, textoDosCentavos } from '@/comuns/dinheiro';
import { diaIso } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { competenciaDoDia } from '@/data/financeiroAvulso';
import type { DetalheDoTitulo, EdicaoDeTitulo } from '@/data/financeiroAvulso';
import type { CategoriaFinanceira, ContatoParaEscolha, TipoDeTitulo } from '@/data/tipos';

export interface LancamentoModalProps {
    /** `{ tipo }` para um lançamento novo; o detalhe do título para editar. */
    alvo: { tipo: TipoDeTitulo } | DetalheDoTitulo;
    categorias: CategoriaFinanceira[];
    /** A preferência "Categoria de receitas": vem escolhida no novo a receber. */
    categoriaPadraoDaReceita?: string;
    onFechar: () => void;
    onSalvo: () => void;
}

/**
 * Novo lançamento e edição de título — o mesmo formulário.
 *
 * A receber é de um paciente; a pagar é de um fornecedor. Conta e forma de
 * pagamento NÃO entram aqui, ao contrário do protótipo: elas são do dinheiro que
 * anda, e se escolhem na baixa — escolhidas agora, envelheceriam até o dia de
 * pagar.
 */
export function LancamentoModal({ alvo, categorias, categoriaPadraoDaReceita, onFechar, onSalvo }: LancamentoModalProps) {
    const aviso = useAviso();
    const idDescricao = useId();
    const idPaciente = useId();
    const idFornecedor = useId();
    const idCategoria = useId();
    const idValor = useId();
    const idVencimento = useId();
    const idCompetencia = useId();
    const idObservacoes = useId();

    const editando = 'titulo' in alvo ? alvo : null;
    const tipoInicial: TipoDeTitulo = 'titulo' in alvo ? alvo.titulo.tipo : alvo.tipo;
    const padraoDoTipo = (t: TipoDeTitulo) => (t === 'receber'
        && categoriaPadraoDaReceita
        && categorias.some((c) => c.id === categoriaPadraoDaReceita && c.natureza === 'receita')
        ? categoriaPadraoDaReceita : '');

    const inicial = {
        tipo: tipoInicial,
        descricao: editando?.titulo.descricao ?? '',
        contatoId: editando?.titulo.contatoId ?? '',
        fornecedor: editando?.fornecedor ?? '',
        categoriaId: editando ? editando.categoriaId ?? '' : padraoDoTipo(tipoInicial),
        valor: editando ? textoDosCentavos(editando.titulo.valorCentavos) : '',
        vencimento: editando?.titulo.vencimento ?? diaIso(),
        competencia: editando ? editando.titulo.competencia.slice(0, 7) : competenciaDoDia(diaIso()),
        observacoes: editando?.observacoes ?? '',
    };

    const [campos, setCampos] = useState(inicial);
    const [competenciaTocada, setCompetenciaTocada] = useState(Boolean(editando));
    /* O paciente escolhido guarda o nome para mostrar; o formulário guarda só o id. */
    const [nomeDoPaciente, setNomeDoPaciente] = useState(editando?.titulo.contatoId ? editando.titulo.contraparte : '');
    const [busca, setBusca] = useState('');
    const [resultados, setResultados] = useState<ContatoParaEscolha[] | null>(null);
    const [erroDosPacientes, setErroDosPacientes] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    /* Busca no servidor, como no orçamento: a lista de 500 deixava de fora quem
       viesse depois, e a clínica não achava o paciente. */
    useEffect(() => {
        if (campos.contatoId || busca.trim().length < 2) {
            setResultados(null);
            return;
        }
        let vivo = true;
        const t = window.setTimeout(() => {
            dados().buscarContatos(busca)
                .then((r) => { if (vivo) { setResultados(r); setErroDosPacientes(null); } })
                .catch((e: unknown) => {
                    if (!vivo) return;
                    setResultados(null);
                    setErroDosPacientes(`A busca falhou: ${e instanceof Error ? e.message : 'tente de novo'}.`);
                });
        }, 250);
        return () => { vivo = false; window.clearTimeout(t); };
    }, [busca, campos.contatoId]);

    const mudar = <C extends keyof typeof campos>(campo: C, valor: (typeof campos)[C]) => setCampos((c) => ({ ...c, [campo]: valor }));

    const valorCentavos = centavosDoTexto(campos.valor);
    const contraparteOk = campos.tipo === 'receber' ? Boolean(campos.contatoId) : Boolean(campos.fornecedor.trim());
    const podeSalvar = Boolean(campos.descricao.trim()) && valorCentavos !== null && valorCentavos > 0 && Boolean(campos.vencimento) && contraparteOk;
    const sujo = JSON.stringify(campos) !== JSON.stringify(inicial);
    const daNatureza = categorias.filter((c) => c.natureza === (campos.tipo === 'receber' ? 'receita' : 'despesa'));

    const salvar = async () => {
        if (!podeSalvar || valorCentavos === null) return;
        setSalvando(true);
        const edicao: EdicaoDeTitulo = {
            descricao: campos.descricao,
            valorCentavos,
            vencimento: campos.vencimento,
            competencia: campos.competencia,
            ...(campos.tipo === 'receber' ? { contatoId: campos.contatoId } : { fornecedor: campos.fornecedor }),
            ...(campos.categoriaId ? { categoriaId: campos.categoriaId } : {}),
            ...(campos.observacoes.trim() ? { observacoes: campos.observacoes } : {}),
        };
        try {
            if (editando) await dados().editarTitulo(editando.titulo.id, edicao);
            else await dados().criarTitulo({ tipo: campos.tipo, ...edicao });
            aviso({
                title: editando ? 'Título atualizado' : 'Lançamento criado',
                description: `${campos.descricao.trim()} · ${campos.tipo === 'receber' ? 'contas a receber' : 'contas a pagar'}`,
            });
            onSalvo();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar o lançamento', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={560} onClose={onFechar}
            title={editando ? 'Editar título' : 'Novo lançamento'}
            description={editando ? 'Valor e vencimento novos entram no fluxo de caixa.' : 'Registre uma entrada ou uma despesa da clínica.'}
            dirty={sujo} onSave={() => { void salvar(); }}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!podeSalvar || salvando}>
                        {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Criar lançamento'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                {!editando && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <SegmentedControl
                            value={campos.tipo} ariaLabel="Tipo do lançamento"
                            onChange={(v) => {
                                const tipo = v as TipoDeTitulo;
                                setCampos((c) => ({ ...c, tipo, categoriaId: padraoDoTipo(tipo) }));
                            }}
                            options={[{ value: 'receber', label: 'A receber' }, { value: 'pagar', label: 'A pagar' }]}
                        />
                    </div>
                )}
                <FormField label="Descrição" htmlFor={idDescricao} required style={{ gridColumn: '1 / -1' }}>
                    <Input id={idDescricao} value={campos.descricao} onChange={(v) => mudar('descricao', v)} placeholder={campos.tipo === 'receber' ? 'Procedimento — paciente' : 'Conta de energia — setembro'} />
                </FormField>

                {campos.tipo === 'receber' ? (
                    <FormField label="Paciente" htmlFor={idPaciente} required error={erroDosPacientes ?? undefined} style={{ gridColumn: '1 / -1' }}>
                        {campos.contatoId ? (
                            <div id={idPaciente} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 36, padding: '0 6px 0 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-control)', background: 'var(--surface)' }}>
                                <Icon name="user-round" size={15} style={{ color: 'var(--neutral-500)' }} />
                                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{nomeDoPaciente || 'Paciente escolhido'}</span>
                                <Button size="sm" variant="ghost" onClick={() => { mudar('contatoId', ''); setNomeDoPaciente(''); setBusca(''); }}>Trocar</Button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <SearchField
                                    value={busca} width="100%" ariaLabel="Buscar paciente" placeholder="Buscar paciente por nome ou telefone"
                                    onChange={(v) => { setBusca(v); setErroDosPacientes(null); }}
                                />
                                {resultados && resultados.length > 0 && (
                                    <ul aria-label="Contatos encontrados" style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                                        {resultados.map((c) => (
                                            <li key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                                <button
                                                    type="button" onClick={() => { mudar('contatoId', c.id); setNomeDoPaciente(c.nome); setResultados(null); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '8px 12px', border: 0, background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)' }}
                                                >
                                                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{c.nome}</span>
                                                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{c.telefone}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {resultados && resultados.length === 0 && (
                                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>Nenhum contato com “{busca.trim()}”. Cadastre em Contatos antes.</p>
                                )}
                            </div>
                        )}
                    </FormField>
                ) : (
                    <FormField label="Fornecedor" htmlFor={idFornecedor} required style={{ gridColumn: '1 / -1' }}>
                        <Input id={idFornecedor} value={campos.fornecedor} onChange={(v) => mudar('fornecedor', v)} placeholder="Enel, Allergan, Imobiliária…" />
                    </FormField>
                )}

                <FormField label="Valor" htmlFor={idValor} required error={campos.valor.trim() && valorCentavos === null ? 'Valor inválido' : undefined}>
                    <Input
                        id={idValor} value={campos.valor} onChange={(v) => mudar('valor', v)} inputMode="decimal" placeholder="0,00"
                        iconLeft={<span style={{ fontSize: 13 }}>R$</span>} invalid={Boolean(campos.valor.trim()) && valorCentavos === null}
                    />
                </FormField>
                <FormField label="Categoria" htmlFor={idCategoria}>
                    <Select
                        id={idCategoria} value={campos.categoriaId} onChange={(v) => mudar('categoriaId', v)} placeholder="Sem categoria"
                        options={daNatureza.map((c) => ({ value: c.id, label: c.nome }))}
                    />
                </FormField>
                <FormField label="Vencimento" htmlFor={idVencimento} required>
                    <Input
                        id={idVencimento} type="date" value={campos.vencimento}
                        onChange={(v) => setCampos((c) => ({ ...c, vencimento: v, competencia: competenciaTocada || !v ? c.competencia : competenciaDoDia(v) }))}
                    />
                </FormField>
                <FormField label="Competência" htmlFor={idCompetencia} hint="O mês a que o valor se refere">
                    <Input id={idCompetencia} type="month" value={campos.competencia} onChange={(v) => { setCompetenciaTocada(true); mudar('competencia', v); }} />
                </FormField>
                <FormField label="Observações" htmlFor={idObservacoes} style={{ gridColumn: '1 / -1' }}>
                    <Textarea id={idObservacoes} rows={2} value={campos.observacoes} onChange={(v) => mudar('observacoes', v)} />
                </FormField>
            </div>
        </Modal>
    );
}
